import { Routes } from '@angular/router';
import { autoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { AppLayout } from '@/app/layout/components/app.layout';
import { currentUserGuard } from '@/app/core/auth/guards/current-user.guard';
import { requirePermission } from '@/app/core/auth/guards/permission.guard';
import { personaHomeGuard, requirePersonaPage } from '@/app/core/auth/guards/persona.guard';
import { OPERATOR_ROUTE_PERMISSIONS } from '@/app/core/navigation/operator-routes';
import { apiClientRegistryGuard } from '@/app/features/api-clients/api-client-registry.guard';
import { TENANT_HUB_ROUTE } from '@/app/features/tenant-hub/tenant-hub.routes';

export const appRoutes: Routes = [
    {
        path: '',
        component: AppLayout,
        canActivate: [autoLoginPartialRoutesGuard, currentUserGuard],
        // Every navigation inside the shell retries what an earlier one failed to read (the actor, the persona's
        // portfolio): a persona left `unresolved` by a passing failure is decided on the next click (T1-07).
        canActivateChild: [currentUserGuard],
        children: [
            // The home route — also where the sign-in callback lands — sends each persona to its own home (T1-07):
            // operator → dashboard, single-tenant admin → its hub, multi-tenant admin → portfolio, user → its applications.
            // A persona that could not be decided stays here and is shown a notice with a retry.
            {
                path: '',
                pathMatch: 'full',
                canActivate: [personaHomeGuard],
                loadComponent: () => import('@/app/features/shared-pages/persona-unresolved.component').then((c) => c.PersonaUnresolvedComponent)
            },
            {
                // The operator's home. Anyone else opening it (the pre-T1-07 home bookmark) is sent to its own home.
                path: 'kontrolna-tabla',
                data: { breadcrumb: 'navigation.dashboard' },
                canActivate: [requirePersonaPage((persona) => persona.isOperator())],
                loadComponent: () => import('@/app/features/dashboard/dashboard.component').then((c) => c.DashboardComponent)
            },
            {
                path: 'pregled',
                data: { breadcrumb: 'navigation.overview' },
                canActivate: [requirePersonaPage((persona) => persona.hasPortfolio())],
                loadComponent: () => import('@/app/features/portfolio/portfolio.component').then((c) => c.PortfolioComponent)
            },
            {
                path: 'moji-tenanti',
                data: { breadcrumb: 'navigation.myTenants' },
                canActivate: [requirePersonaPage((persona) => persona.hasTenantList())],
                loadComponent: () => import('@/app/features/portfolio/my-tenants.component').then((c) => c.MyTenantsComponent)
            },
            {
                // People across a tenant administrator's tenants and their CSV (T1-08). Anyone else is sent to its own home.
                path: 'ljudi',
                data: { breadcrumb: 'navigation.people' },
                canActivate: [requirePersonaPage((persona) => persona.hasPeople())],
                loadComponent: () => import('@/app/features/people/people.component').then((c) => c.PeopleComponent)
            },
            {
                path: 'moje-aplikacije',
                data: { breadcrumb: 'navigation.myApplications' },
                loadComponent: () => import('@/app/features/moje-aplikacije/moje-aplikacije.component').then((c) => c.MojeAplikacijeComponent)
            },
            {
                path: 'ekosistem',
                data: { breadcrumb: 'navigation.ecosystem', fullWidth: true },
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/ekosistem'])],
                loadComponent: () => import('@/app/features/service-graph/service-graph.component').then((c) => c.ServiceGraphComponent)
            },
            {
                path: 'moj-nalog',
                data: { breadcrumb: 'navigation.myAccount' },
                loadComponent: () => import('@/app/features/moj-nalog/moj-nalog.component').then((c) => c.MojNalogComponent)
            },
            {
                path: 'tenanti',
                data: { breadcrumb: 'navigation.tenants' },
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/tenanti'])],
                loadComponent: () => import('@/app/features/tenants/tenants.component').then((c) => c.TenantsComponent)
            },
            TENANT_HUB_ROUTE,
            {
                path: 'grupe',
                data: { breadcrumb: 'navigation.groups' },
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/grupe'])],
                loadComponent: () => import('@/app/features/grupe/grupe.component').then((c) => c.GrupeComponent)
            },
            {
                path: 'korisnici',
                data: { breadcrumb: 'navigation.users' },
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/korisnici'])],
                loadComponent: () => import('@/app/features/users/users.component').then((c) => c.UsersComponent)
            },
            {
                // One account's page (UX pregled 2, D-UX2-7): a registry row and a global-search hit open it. The registry's own
                // guard, so every link to it is offered exactly where the registry's is.
                path: 'korisnici/:id',
                data: { breadcrumb: 'navigation.user' },
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/korisnici'])],
                loadComponent: () => import('@/app/features/users/user-account.component').then((c) => c.UserAccountComponent)
            },
            {
                path: 'aplikacije',
                data: { breadcrumb: 'navigation.applications' },
                loadComponent: () => import('@/app/features/applications/applications.component').then((c) => c.ApplicationsComponent)
            },
            {
                // One application's page (UX pregled 3, D-UX3-2): a registry row, a global-search hit and the old `?id=` link open
                // it. No permission guard — the registry has none; the page reads what the actor's flags allow.
                path: 'aplikacije/:id',
                data: { breadcrumb: 'navigation.application' },
                loadComponent: () => import('@/app/features/applications/application-page.component').then((c) => c.ApplicationPageComponent)
            },
            {
                path: 'lease',
                data: { breadcrumb: 'navigation.accessAndLeases' },
                loadComponent: () => import('@/app/features/leases/leases.component').then((c) => c.LeasesComponent)
            },
            {
                // The registry across tenants (UX pregled 4, D-UX4-1): the global-flag holder reads it, a one-tenant scope whose hub
                // tab opens goes to that tab, anyone else to /zabranjen-pristup.
                path: 'api-klijenti',
                data: { breadcrumb: 'navigation.apiClients' },
                canActivate: [apiClientRegistryGuard],
                loadComponent: () => import('@/app/features/api-clients/api-clients.component').then((c) => c.ApiClientsComponent)
            },
            {
                // The three „Federacija“ screens (UX pregled 5, D-UX5-1): the crumb names the group, then the screen; /federacija now
                // has the consumers VIEW guard of its two siblings.
                path: 'federacija',
                data: { breadcrumb: ['navigation.federation', 'navigation.ssoApplications'] },
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/federacija'])],
                loadComponent: () => import('@/app/features/federation/federation.component').then((c) => c.FederationComponent)
            },
            {
                // One SSO application's page (UX pregled 5, D-UX5-8): a registry row and its name open it. The registry's own guard;
                // its crumb is the application's name (the child route's resolver, `sso-application-page.routes.ts`).
                path: 'federacija/:publicId',
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/federacija'])],
                loadChildren: () => import('@/app/features/federation/sso-application-page.routes').then((r) => r.SSO_APPLICATION_PAGE_ROUTES)
            },
            {
                path: 'entra-federacija',
                data: { breadcrumb: ['navigation.federation', 'navigation.entraOrganizations'] },
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/entra-federacija'])],
                loadComponent: () => import('@/app/features/entra-federacija/entra-federacija.component').then((c) => c.EntraFederacijaComponent)
            },
            {
                // The external identity provider registry (Identity ADR 0011): rows in the database, configuration entries read-only.
                path: 'provajderi-prijave',
                data: { breadcrumb: ['navigation.federation', 'navigation.loginProviders'] },
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/provajderi-prijave'])],
                loadComponent: () => import('@/app/features/provajderi-prijave/provajderi-prijave.component').then((c) => c.ProvajderiPrijaveComponent)
            },
            {
                // One provider's page (UX pregled 5, D-UX5-21): a registry row and its name open it; the registry's own guard. Its crumb
                // is the provider's name (the child route's resolver, `login-provider-page.routes.ts`).
                path: 'provajderi-prijave/:key',
                canActivate: [requirePermission(OPERATOR_ROUTE_PERMISSIONS['/provajderi-prijave'])],
                loadChildren: () => import('@/app/features/provajderi-prijave/login-provider-page.routes').then((r) => r.LOGIN_PROVIDER_PAGE_ROUTES)
            },
            {
                path: 'obavestenja',
                data: { breadcrumb: 'navigation.announcements' },
                loadComponent: () => import('@/app/features/announcements/announcements.component').then((c) => c.AnnouncementsComponent)
            },
            {
                // One announcement's page (UX pregled 4, D-UX4-12): a registry row and its name open it. No permission guard — the
                // registry has none; the page reads what the actor's flags allow. Its crumb is the announcement's name (the child
                // route's resolver, `announcement-page.routes.ts`).
                path: 'obavestenja/:id',
                loadChildren: () => import('@/app/features/announcements/announcement-page.routes').then((r) => r.ANNOUNCEMENT_PAGE_ROUTES)
            },
            {
                path: 'zabranjen-pristup',
                data: { breadcrumb: 'navigation.accessDenied' },
                loadComponent: () => import('@/app/features/shared-pages/access-denied.component').then((c) => c.AccessDeniedComponent)
            }
        ]
    },
    {
        path: '**',
        component: AppLayout,
        canActivate: [autoLoginPartialRoutesGuard, currentUserGuard],
        children: [{ path: '', loadComponent: () => import('@/app/features/shared-pages/not-found.component').then((c) => c.NotFoundComponent) }]
    }
];
